import { AxiosResponse } from "axios";
import { axiosInstance } from "./axiosInstance";
import { EnrollmentDueDayBody, EnrollmentDueDayDTO } from "../../models/EnrollmentDueDayDTO";
import {
	ApplyDecisions, FloatingContactedDTO, FloatingListDTO, FloatingQuery, LifecycleDTO, NewUpdateBody, ReentryProjectionDTO, ScheduleRosterDTO, UpdateDetailsDTO,
	UpdateListDTO, UpdateOptionsDTO, UpdateTray
} from "../../models/EnrollmentUpdateDTO";

const RESOURCE_URL_V2 = "/admin/campus/";

const query = (params: { [k: string]: any }): string => {
	const parts = Object.keys(params)
		.filter((k) => params[k] !== undefined && params[k] !== null && params[k] !== "")
		.map((k) => `${k}=${encodeURIComponent(String(params[k]))}`);
	return parts.length ? "?" + parts.join("&") : "";
};

/**
 * Actualizaciones de matrícula (sección enrollmentUpdates). The ids travel in the query/body because the
 * base Controller derives the permission section from the path segment before the "_action".
 */
export class EnrollmentUpdatesService {

	private base = (campusId: number) => `${RESOURCE_URL_V2}${campusId}/enrollmentUpdates`;

	/** 403 when the role has no READ on enrollmentUpdates. */
	getDueDay = async (campusId: number, enrollmentId: number): Promise<AxiosResponse<EnrollmentDueDayDTO>> => {
		return axiosInstance.get<EnrollmentDueDayDTO>(`${this.base(campusId)}/_dueDay?enrollmentId=${enrollmentId}`);
	}

	/** 403 `{message}` unless Supervisión de Caja / Contraloría / superAdmin; 422 on a bad day or note. */
	setDueDay = async (campusId: number, body: EnrollmentDueDayBody): Promise<AxiosResponse<EnrollmentDueDayDTO>> => {
		return axiosInstance.put<EnrollmentDueDayDTO>(`${this.base(campusId)}/_dueDay`, body);
	}

	list = async (campusId: number, tray: UpdateTray, filters: { typeId?: number | null; scheduleId?: number | null; batchId?: number | null;
		enrollmentId?: number | null; studentId?: number | null; search?: string; perPage?: number; page?: number; typeIds?: string; requesterIds?: string; scheduleIds?: string } = {}): Promise<AxiosResponse<UpdateListDTO>> => {
		return axiosInstance.get<UpdateListDTO>(this.base(campusId) + query({ tray, perPage: 50, page: 1, ...filters }));
	}

	details = async (campusId: number, id: number): Promise<AxiosResponse<UpdateDetailsDTO>> => {
		return axiosInstance.get<UpdateDetailsDTO>(`${this.base(campusId)}/_details?id=${id}`);
	}

	options = async (campusId: number, enrollmentId: number): Promise<AxiosResponse<UpdateOptionsDTO>> => {
		return axiosInstance.get<UpdateOptionsDTO>(`${this.base(campusId)}/_options?enrollmentId=${enrollmentId}`);
	}

	scheduleRoster = async (campusId: number, scheduleId: number): Promise<AxiosResponse<ScheduleRosterDTO>> => {
		return axiosInstance.get<ScheduleRosterDTO>(`${this.base(campusId)}/_scheduleRoster?scheduleId=${scheduleId}`);
	}

	/** Flotantes o Por Cerrar, de la más reciente a la más vieja, con conteos por motivo (los filtros no afectan los conteos del motivo). */
	floating = async (campusId: number, filters: FloatingQuery = {}): Promise<AxiosResponse<FloatingListDTO>> => {
		return axiosInstance.get<FloatingListDTO>(`${this.base(campusId)}/_floating` + query(filters));
	}

	/** Se llama al abrir el WhatsApp de una flotante; 422 si ya no está Aplazada ni Sin horario. */
	floatingContacted = async (campusId: number, enrollmentId: number): Promise<AxiosResponse<FloatingContactedDTO>> => {
		return axiosInstance.post<FloatingContactedDTO>(`${this.base(campusId)}/_floatingContacted`, { enrollmentId });
	}

	/** from/to son días en la zona del plantel; statusId acepta "1,2". */
	log = async (campusId: number, filters: { from?: string; to?: string; typeId?: number | null; statusId?: number | string | null; requestedBy?: number | null;
		appliedBy?: number | null; scheduleId?: number | null; studentId?: number | null; page?: number }): Promise<AxiosResponse<UpdateListDTO>> => {
		return axiosInstance.get<UpdateListDTO>(`${this.base(campusId)}/_log` + query(filters));
	}

	reentryProjection = async (campusId: number, enrollmentId: number, scheduleId: number): Promise<AxiosResponse<ReentryProjectionDTO>> => {
		return axiosInstance.get<ReentryProjectionDTO>(`${this.base(campusId)}/_reentryProjection?enrollmentId=${enrollmentId}&scheduleId=${scheduleId}`);
	}

	/** One update or a batch (items). applyNow = Caja crea y aplica en un paso. */
	create = async (campusId: number, body: NewUpdateBody): Promise<AxiosResponse<{ created: number; updates: UpdateDetailsDTO[] }>> => {
		return axiosInstance.post(this.base(campusId), body);
	}

	apply = async (campusId: number, id: number, decisions: ApplyDecisions): Promise<AxiosResponse<UpdateDetailsDTO>> => {
		return axiosInstance.put<UpdateDetailsDTO>(`${this.base(campusId)}/_apply`, { id, decisions });
	}

	/** Solo Caja: el dinero del reparto regresa a sus mensualidades de origen. 422 si el destino ya no tiene ese dinero. */
	undoReparto = async (campusId: number, auditItemId: number, note: string): Promise<AxiosResponse<{ auditItemId: number; undone: number; amount: number }>> => {
		return axiosInstance.put(`${this.base(campusId)}/_undoReparto`, { auditItemId, note });
	}

	returnUpdate = async (campusId: number, id: number, reason: string): Promise<AxiosResponse<UpdateDetailsDTO>> => {
		return axiosInstance.put<UpdateDetailsDTO>(`${this.base(campusId)}/_return`, { id, reason });
	}

	resubmit = async (campusId: number, id: number, body: { comment?: string; note?: string; [k: string]: any }): Promise<AxiosResponse<UpdateDetailsDTO>> => {
		return axiosInstance.put<UpdateDetailsDTO>(`${this.base(campusId)}/_resubmit`, { id, ...body });
	}

	discard = async (campusId: number, id: number, reason?: string): Promise<AxiosResponse<UpdateDetailsDTO>> => {
		return axiosInstance.put<UpdateDetailsDTO>(`${this.base(campusId)}/_discard`, { id, reason });
	}

	setType = async (campusId: number, id: number, body: { typeId: number; toScheduleId?: number | null; toCourseId?: number | null; options?: any; note?: string }): Promise<AxiosResponse<UpdateDetailsDTO>> => {
		return axiosInstance.put<UpdateDetailsDTO>(`${this.base(campusId)}/_setType`, { id, ...body });
	}

	/** Maestras del plantel con sus horarios abiertos. */
	mentors = async (campusId: number): Promise<AxiosResponse<{ id: number; name: string; phone: string | null; enabled: boolean; openSchedules: number }[]>> => {
		return axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/schedules/_mentors`);
	}

	/* Horarios (sección schedules): cierre, fusión y cambio de maestra. */

	lifecycle = async (campusId: number, scheduleId: number): Promise<AxiosResponse<LifecycleDTO>> => {
		return axiosInstance.get<LifecycleDTO>(`${RESOURCE_URL_V2}${campusId}/schedules/${scheduleId}/_lifecycle`);
	}

	closeSchedule = async (campusId: number, scheduleId: number, reason: string): Promise<AxiosResponse<LifecycleDTO>> => {
		return axiosInstance.put<LifecycleDTO>(`${RESOURCE_URL_V2}${campusId}/schedules/${scheduleId}/_close`, { reason });
	}

	mergeSchedules = async (campusId: number, scheduleId: number, sourceIds: number[], targetId: number, reason?: string): Promise<AxiosResponse<LifecycleDTO>> => {
		return axiosInstance.put<LifecycleDTO>(`${RESOURCE_URL_V2}${campusId}/schedules/${scheduleId}/_merge`, { sourceIds, targetId, reason });
	}

	changeMentor = async (campusId: number, scheduleId: number, mentorId: number, reason?: string): Promise<AxiosResponse<LifecycleDTO>> => {
		return axiosInstance.put<LifecycleDTO>(`${RESOURCE_URL_V2}${campusId}/schedules/${scheduleId}/_mentor`, { mentorId, reason });
	}
}
